import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { InjectedFormProps, reduxForm } from 'redux-form'
import HtmlWithStyled from '../../components/HtmlWithStyled'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import Image from '../../components/common/Image'
import Loading from '../../components/common/Loading'
import Text from '../../components/common/Text'
import * as paths from '../../constant/path'
import { clearSteps } from '../../redux-store/enrollment-reducer'
import {
  useGetAllSelectedBenefits,
  useGetAllSelfBenefitsType,
  useGetEnrollmentSelectPeriod,
  useGetEnrollmentActivePeriod,
  useSelfBenefitsTypeSend
} from '../../services/enrollment/enrollment-query'
import { BenefitChoices, BenefitType, BenefitsCategoryWrapperList, BenefitsCategoryCode, SelectSelfBenefitTypeParams } from '../../services/enrollment/enrollment-types'
import { useGetUser } from '../../services/user/user-query'
import { useVisibility } from '../../utils/custom-hook'
import { mobile, useScreen } from '../../utils/responsive-helper'
import { useRouter, useSelectedSelfBenefitTypePresent } from '../../utils/helper'
import FooterContent from './FooterContent'
import ModalAcceptPrivacy from './ModalAcceptPrivacy'
import { SubGroupBenefitsField } from './SubGroupBenefits'
import Welfare from './Welfare'


const Layout = styled.div`
  width: 768px;
  margin: 0 auto;
  z-index: 1;
  > * {
    margin-top: 24px;
  }

  ${mobile} {
    width: 100%;
  }
`
const ProfileLayout = styled.div`
  display: flex;
  justify-content: space-between;
  z-index: inherit;
  margin-bottom: 32px;

  .left {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  ${mobile} {
    padding: 0px 16px;
  }
`
const CustomStyleCard = styled(Card)`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  width: 100%;
  margin-bottom: 24px;
  padding: 32px 24px;
`
const BackgroundHeader = styled.div`
  background-color: #E7E2D8;
  width: calc(100% - 32px);
  height: 266px;
  margin-top: 0px !important;
  position: absolute;

  ${mobile} {
    width: 100%;
  }
`
const CardLayout = styled(Card)`
  width: 100%;
  max-width: 784px;
  margin: 0 auto;
  padding: 56px 90px;
  box-shadow: 0px 4px 8px rgba(113, 113, 113, 0.1);
  border-radius: 8px;

  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  row-gap: 32px;

  ${mobile} {
    padding: 56px 24px;
  }
`

const BenefitLayout = styled.div`
  background: #FAF9F6;
  border: 1px solid #E7E2D8;
  border-radius: 8px;
  padding: 16px;

  display: flex;
  flex-flow: column;
  align-items: center;
  row-gap: 16px;
`

export const useGetEnrollmentYear = (startDateProp: number = 0) => {
  const { data: enrollmentPeriod } = useGetEnrollmentSelectPeriod()

  return useMemo(() => {
    let startDate
    if (startDateProp) {
      startDate = startDateProp
    } else {
      startDate = enrollmentPeriod?.startEnrollmentDate
    }
    const enrollmentYear = dayjs(startDate).add(543, 'year').year()
    return {
      enrollmentYear: enrollmentYear || '',
    }
  }, [enrollmentPeriod, startDateProp])
}

const ENROLLMENT_FORM_NAME = 'Enrollment'
const enchancer = reduxForm({ form: ENROLLMENT_FORM_NAME })
type EditSelfBenefitsFormValues = {
  [key: string]: Partial<BenefitChoices>[]
}

const Enrollment = (props: InjectedFormProps<EditSelfBenefitsFormValues>) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const { isMobile } = useScreen()
  const { query } = useRouter<{ summary: string }>()
  // enrollment
  const { data: enrollmentSelectPeriod, isError: isErrorSelectPeriod, isFetching: isFetchingSelectPeriod } = useGetEnrollmentSelectPeriod()
  // period
  const { data: enrollmentActivePeriod } = useGetEnrollmentActivePeriod()
  const { data: user } = useGetUser()

  const isEnrollmentSelect: boolean = useMemo(() => {
    if (isFetchingSelectPeriod) return false

    if (isErrorSelectPeriod) {
      return false
    } else {
      return true
    }
  }, [isErrorSelectPeriod, isFetchingSelectPeriod])

  const { data: selfBenefitsListEnrollment } = useGetAllSelfBenefitsType(enrollmentSelectPeriod?.benefitYearId)
  const { data: selfBenefitsListPeriod } = useGetAllSelfBenefitsType(enrollmentActivePeriod?.benefitYearId)
  const { data: selectedSelfBenefitsEnrollment } = useGetAllSelectedBenefits(enrollmentSelectPeriod?.benefitYearId)
  const { data: selectedSelfBenefitsPeriod } = useGetAllSelectedBenefits(enrollmentActivePeriod?.benefitYearId)

  const selfBenefitsList = useMemo(() => {
    return isEnrollmentSelect ? selfBenefitsListEnrollment : selfBenefitsListPeriod
  }, [isEnrollmentSelect, selfBenefitsListEnrollment, selfBenefitsListPeriod])

  const selectedSelfBenefits = useMemo(() => {
    return isEnrollmentSelect ? selectedSelfBenefitsEnrollment : selectedSelfBenefitsPeriod
  }, [isEnrollmentSelect, selectedSelfBenefitsEnrollment, selectedSelfBenefitsPeriod])

  const selectedSelfBenefitId = useMemo(() => {
    return selectedSelfBenefits?.selectedSelfBenefitId
  }, [selectedSelfBenefits])

  const { mutate: selfBenefitsTypeSend } = useSelfBenefitsTypeSend()
  const [isSummary, setIsSummary] = useState<boolean>(true)

  useEffect(() => {
    if (query.summary === 'false') {
      setIsSummary(false)
    }
  }, [query.summary])

  const [visible, modalAction] = useVisibility();
  const { isFlexi } = useSelectedSelfBenefitTypePresent()
  const [isLoading, setIsLoading] = useState(true)

  const FlexiID = useMemo(() => {
    return selfBenefitsList?.find((item) => item.selfBenefitsName === BenefitType.FLEXi)?.id
  }, [selfBenefitsList])

  const isExpired = useMemo(() => {
    if (isFetchingSelectPeriod) return

    if (isErrorSelectPeriod) {
      return true
    } else {
      return false
    }
  }, [isErrorSelectPeriod, isFetchingSelectPeriod])

  const benefitType: BenefitsCategoryWrapperList | undefined = useMemo(() => {
    if (!selfBenefitsList) return

    const choices: BenefitChoices[] =
      selfBenefitsList.sort((a, b) => b.id - a.id)
        .map((item) => {
          return {
            id: item.id,
            choicesName: item.selfBenefitsName,
            choicesDescription: item.description,
            taggedPrice: item.selfBenefitsName === BenefitType.FLEXi ? 12000 : 0,
            defaultCoinValue: item.selfBenefitsName === BenefitType.FLEXi ? 12000 : 0,
            consentDetails: '',
            selected: selectedSelfBenefitId === item.id ? true : false,
            isDefault: false,
          }
        })

    return {
      benefitsCategoryId: 1,
      benefitsCategoryName: "เลือกประเภทสิทธิ์",
      benefitsCategoryCode: BenefitsCategoryCode.SELF,
      choices,
    }
  }, [selfBenefitsList, selectedSelfBenefitId])

  const benefit: BenefitsCategoryWrapperList = useMemo(() => {
    return {
      benefitsCategoryId: 1,
      benefitsCategoryName: 'เลือกประเภทสิทธิ์',
      benefitsCategoryCode: BenefitsCategoryCode.SELF,
      choices: [
        {
          id: 1,
          choicesName: BenefitType.Traditional,
          choicesDescription: `<table class="table table-bordered" border="1" style=" background-color: rgb(255, 255, 255); width: 100%; max-width: 100%; border-top-style: hidden; border-right-style: hidden; border-left-style: hidden; border-collapse: collapse; border-spacing: 0; "> <tbody> <tr style="border-bottom: hidden"> <td> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 24px 16px; font-size: 20px; margin: 0;"> รายละเอียด </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> วงเงินค่ารักษาพยาบาลส่วนเกิน OPD : 12,000 OPD บาท/ปี </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> (6000 แรก/6000 หลัง​) </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 180px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> FLEXi Coin: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> ไม่มี </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 170px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> บัตรประกันสุขภาพของคุณ: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> แผนเดิม </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> บัตรประกันสุขภาพคู่สมรส-บุตร: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 1ุpx; margin: 0;"> แผนเดิม </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 500px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> รายการสวัสดิการอื่นๆ </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> เบิกเงินช่วยเหลือค่าออกกำลังกาย : </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> - BKK: ตามจริงไม่เกิน 1,500 บาท/ปี </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> - RYG: ในเครือข่าย ลงชื่อเล่นตามจริง / นอกเครือข่าย ตามจริงไม่เกิน 500 บ./เดือน </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> ฉีดวัคซีนป้องกันไข้หวัดใหญ่ประจำปี: ตามที่บริษัทฯจัดให้ </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> เบิกเงินช่วยเหลือค่าฉีดวัคซีนไวรัสตับเอบี: 50% ของค่าวัคซีน 1 ครั้งตลอดอายุงานใน BST </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> Market place: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> ไม่มี </p> </td> </tr> </tbody> </table>`,
          taggedPrice: 0,
          defaultCoinValue: 0,
          consentDetails: '',
          selected: false,
          isDefault: false
        },
        {
          id: 2,
          choicesName: BenefitType.FLEXi,
          choicesDescription: `<table class="table table-bordered" border="1" style=" background-color: rgb(255, 255, 255); width: 100%; max-width: 100%; border-top-style: hidden; border-right-style: hidden; border-left-style: hidden; border-collapse: collapse; border-spacing: 0; "> <tbody> <tr style="border-bottom: hidden"> <td> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 24px 16px; font-size: 20px; margin: 0;"> รายละเอียด </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> วงเงินค่ารักษาพยาบาลส่วนเกิน OPD : 12,000 OPD บาท/ปี </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> (6000 แรก/6000 หลัง​) </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 180px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> FLEXi Coin: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> 12,000 บาท/ปี </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> ใช้แลกสวัสดิการ FLEXi Item </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 170px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> บัตรประกันสุขภาพของคุณ: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> เลือกแผนประกันฯ ได้เอง (FLEXi Insurance) </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> บัตรประกันสุขภาพคู่สมรส-บุตร: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 1ุpx; margin: 0;"> แผนเดิม </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4; height: 500px; vertical-align: baseline;"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> รายการสวัสดิการอื่นๆ </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> ตามรายการ FLEXi Items ที่บริษัทฯ กำหนดไว้ ได้แก่ </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> หมวด Physical, Mental, Financial, Social </p> </td> </tr> <tr> <td style="padding: 16px; border-bottom: 1px solid #c4c4c4"> <p style=" color: #363640; font-weight: 700; text-align: center; padding: 8px 16px; font-size: 18px; margin: 0;"> Market place: </p> <p style=" color: #363640; text-align: center; padding: 8px 16px; font-size: 16px; margin: 0;"> แลกซื้อสินค้าได้ ตามร้านค้าที่มีในระบบ </p> </td> </tr> </tbody> </table>`,
          taggedPrice: 12000,
          defaultCoinValue: 12000,
          consentDetails: '',
          selected: false,
          isDefault: false
        }
      ]
    }
  }, [])

  const createTraditional = useCallback((id: number) => {
    history.push(paths.enrollmentTraditionalSummary({
      queryParam: {
        id,
      }
    }))
  }, [history])

  const handleSummary = useCallback(() => {
    setIsSummary(false)
  }, [])

  const handleSelectedFlexBenefit = useCallback(() => {
    if (!FlexiID) return

    const isTraditionalPast = selfBenefitsListPeriod?.find(item => item.selfBenefitsName === BenefitType.Traditional)?.id === selectedSelfBenefitsPeriod?.selectedSelfBenefitId
    if (isEnrollmentSelect && isTraditionalPast) {
      setIsSummary(false)
    } else {
      history.push(paths.benefit({ queryParam: { id: FlexiID } }))
    }
  }, [history, FlexiID, selectedSelfBenefitsPeriod, selfBenefitsListPeriod, isEnrollmentSelect])

  const createFlex = useCallback(() => {
    if (!FlexiID || !enrollmentSelectPeriod) return;

    const params: SelectSelfBenefitTypeParams = {
      selfBenefitTypeId: FlexiID,
      benefitYearId: enrollmentSelectPeriod?.benefitYearId
    }

    selfBenefitsTypeSend(params, {
      onSuccess: () => {
        dispatch(
          clearSteps()
        )

        setIsLoading(false)
        history.push(
          paths.benefit({ queryParam: { id: FlexiID } })
        )
      }
    })
  }, [FlexiID, dispatch, history, selfBenefitsTypeSend, enrollmentSelectPeriod])

  useEffect(() => {
    if (!FlexiID) return

    if (isFlexi && !isErrorSelectPeriod) {
      history.push(
        paths.benefit({ queryParam: { id: FlexiID } })
      )
    } else {
      if (!(query.summary && query.summary === 'false')) {
        setIsSummary(true)
      }
      setIsLoading(false)
    }
  }, [isFlexi, createFlex, FlexiID, history, enrollmentSelectPeriod, isErrorSelectPeriod, query.summary])

  const onSubmit = useCallback((next: () => void, type: BenefitType, id: number) => {
    if (!type) return;

    if (type === BenefitType.Traditional) {
      createTraditional(id)
    } else if (type === BenefitType.FLEXi) {
      modalAction.show()
    }
  }, [createTraditional, modalAction])

  const convertDateToThai = (date?: number) => {
    const dateThai = dayjs(date).format('DD MMMM')
    return dateThai
  }

  const SummaryBenefitSeleted = useMemo(() => {
    if (selfBenefitsList?.find(item => item.selfBenefitsName === BenefitType.Traditional)?.id === selectedSelfBenefitId) {
      return (
        <Layout style={{ marginTop: 24 }}>
          <CardLayout>
            <Image imageName='GroupImage' />
            <Text size={20} type="Bold">เลือกแผนสวัสดิการสำเร็จ</Text>
            <BenefitLayout>
              <Text size={18} type="Medium">สรุปการเลือกแผนสวัสดิการของคุณ​</Text>
              <Text>แผนสวัสดิการ : Traditional</Text>
            </BenefitLayout>
            {!isExpired
              ? <Button onClick={handleSummary}>แก้ไขแผนสวัสดิการ</Button>
              : <Text size={18} type="Medium">หมดเขตการเลือกแผนสวัสดิการ</Text>}
          </CardLayout>
        </Layout>
      )
    } else if (selectedSelfBenefits && selectedSelfBenefits.selectedChoices.length > 0 && selfBenefitsList?.find(item => item.selfBenefitsName === BenefitType.FLEXi)?.id === selectedSelfBenefitId) {
      return (
        <Layout style={{ marginTop: 24 }}>
          <CardLayout>
            <Image imageName='GroupImage' />
            <Text size={20} type="Bold">เลือกแผนสวัสดิการสำเร็จ</Text>
            <BenefitLayout>
              <Text size={18} type="Medium">สรุปการเลือกแผนสวัสดิการของคุณ​</Text>
              <Text>แผนสวัสดิการ : {selectedSelfBenefits?.selectedChoices[1].selfBenefitsName}</Text>
              {!isExpired ? (
                <>
                  <Text>คุณได้รับความคุ้มครองตามแผน : {selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoiceName}</Text>
                </>
              ) : (
                <>
                  <Text>คุณได้รับความคุ้มครองตามแผน : {selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoiceName}</Text>
                  <HtmlWithStyled className="benefit-description" html={selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoice.choicesDescription} />
                </>
              )}
              <Text>แลกบัตรประกันสุขภาพของคู่สมรส-บุตร : {selectedSelfBenefits?.selectedChoices[1].selfBenefitsChoiceName}</Text>
            </BenefitLayout>
            {!isExpired
              ? <Button onClick={handleSelectedFlexBenefit}>แก้ไขแผนสวัสดิการ</Button>
              : <Text size={18} type="Medium">หมดเขตการเลือกแผนสวัสดิการ</Text>}
          </CardLayout>
        </Layout >
      )
    } else {
      return (
        <>
          <BackgroundHeader />
          <Layout>
            <ProfileLayout>
              <div className='left'>
                <Text size={32} type="Bold">{`${user?.firstName} ${user?.lastName}`}</Text>
                <Text size={14} type="Bold">{t('home.subtitle')}</Text>
              </div>
            </ProfileLayout>
            <CustomStyleCard>
              <Welfare
                iconPath={'iconUser'}
                title={'เลือกแผนสวัสดิการ (Traditional / FLEXi)'}
                descriptions={[`มีผลวันที่ ${convertDateToThai(enrollmentSelectPeriod?.startPeriodDate)} - ${convertDateToThai(enrollmentSelectPeriod?.endPeriodDate)} ${dayjs(enrollmentSelectPeriod?.endPeriodDate).add(543, 'year').format('YYYY')}`]}
              />
              <SubGroupBenefitsField
                key={`${benefit.benefitsCategoryId}`}
                name={'ChoiceTypeSelected'}
                benefit={benefitType || benefit}
                numberGroupShow={isMobile ? 1 : 2}
                textButtonSelect='เลือกแผนสวัสดิการ'
                textSelected='แผนที่คุณเลือก'
              />
              <FooterContent onSubmit={onSubmit} />
            </CustomStyleCard>
          </Layout>
        </>
      )
    }
  }, [selectedSelfBenefits, enrollmentSelectPeriod, selfBenefitsList, user, isExpired, benefit, isMobile, handleSummary, handleSelectedFlexBenefit, benefitType, t, onSubmit, selectedSelfBenefitId])

  const SelectChoiceBenefitType = useMemo(() => {
    if (!selectedSelfBenefits || !selfBenefitsList) return

    if (selfBenefitsList?.find(item => item.selfBenefitsName === BenefitType.Traditional)?.id === selectedSelfBenefitId) {
      return (
        <Layout style={{ marginTop: 24 }}>
          <CardLayout>
            <Image imageName='GroupImage' />
            <Text size={20} type="Bold">เลือกแผนสวัสดิการสำเร็จ</Text>
            <BenefitLayout>
              <Text size={18} type="Medium">สรุปการเลือกแผนสวัสดิการของคุณ​</Text>
              <Text>แผนสวัสดิการ : Traditional</Text>
            </BenefitLayout>
            {!isExpired
              ? <Button onClick={handleSummary}>แก้ไขแผนสวัสดิการ</Button>
              : <Text size={18} type="Medium">หมดเขตการเลือกแผนสวัสดิการ</Text>}
          </CardLayout>
        </Layout>
      )
    } else if (selectedSelfBenefits.selectedChoices.length > 0) {
      return (
        <Layout style={{ marginTop: 24 }}>
          <CardLayout>
            <Image imageName='GroupImage' />
            <BenefitLayout>
              <Text size={18} type="Medium">สรุปการเลือกแผนสวัสดิการของคุณ​</Text>
              <Text>แผนสวัสดิการ : {selectedSelfBenefits.selectedChoices[1].selfBenefitsName}</Text>
              {!isExpired ? (
                <>
                  <Text>คุณได้รับความคุ้มครองตามแผน : {selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoiceName}</Text>
                </>
              ) : (
                <>
                  <Text>คุณได้รับความคุ้มครองตามแผน : {selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoiceName}</Text>
                  <HtmlWithStyled className="benefit-description" html={selectedSelfBenefits?.selectedChoices[0].selfBenefitsChoice.choicesDescription!} />
                </>
              )}
              <Text>แลกบัตรประกันสุขภาพของคู่สมรส-บุตร : {selectedSelfBenefits?.selectedChoices[1].selfBenefitsChoiceName}</Text>
            </BenefitLayout>
            <Text size={18} type="Medium">ไม่อยู่ในช่วงของการเลือกแผนสวัสดิการ</Text>
          </CardLayout>
        </Layout >
      )
    }

  }, [selectedSelfBenefits, isExpired, selfBenefitsList, selectedSelfBenefitId, handleSummary])

  return (
    <Loading loading={isLoading}>
      <ModalAcceptPrivacy
        visible={visible}
        onConfirmClick={createFlex}
        onCloseClick={modalAction.hide}
      />
      {isEnrollmentSelect ? (
        <>
          {selectedSelfBenefits && selectedSelfBenefits.selectedSelfBenefitId && isSummary ? (
            <>
              {SummaryBenefitSeleted}
            </>
          ) : (
            <>
              <BackgroundHeader />
              <Layout>
                <ProfileLayout>
                  <div className='left'>
                    <Text size={32} type="Bold">{`${user?.firstName} ${user?.lastName}`}</Text>
                    <Text size={14} type="Bold">{t('home.subtitle')}</Text>
                  </div>
                </ProfileLayout>
                <CustomStyleCard>
                  <Welfare
                    iconPath={'iconUser'}
                    title={'เลือกแผนสวัสดิการ (Traditional / FLEXi)'}
                    descriptions={[`มีผลวันที่ ${convertDateToThai(enrollmentSelectPeriod?.startPeriodDate)} - ${convertDateToThai(enrollmentSelectPeriod?.endPeriodDate)} ${dayjs(enrollmentSelectPeriod?.endPeriodDate).add(543, 'year').format('YYYY')}`]}
                  />
                  <SubGroupBenefitsField
                    key={`${benefit.benefitsCategoryId}`}
                    name={'ChoiceTypeSelected'}
                    benefit={benefitType || benefit}
                    numberGroupShow={isMobile ? 1 : 2}
                    textButtonSelect='เลือกแผนสวัสดิการ'
                    textSelected='แผนที่คุณเลือก'
                  />
                  <FooterContent onSubmit={onSubmit} />
                </CustomStyleCard>
              </Layout>
            </>
          )}
        </>
      ) : (
        <>{SelectChoiceBenefitType}</>
      )}
    </Loading>
  )
}

export default enchancer(Enrollment)
